import { useEffect, useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

export default function LMSChangePassword() {
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    lmsApi
      .get("/auth/me")
      .catch(() =>
        navigate("/learn/login", {
          replace: true,
        })
      );
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");

    if (newPassword !== confirmPassword) {
      setError("The new passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      await lmsApi.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });

      navigate("/learn", {
        replace: true,
      });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "We could not change your password."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Change Password | TRAC-Learn"
        description="Secure TRAC-Learn password update."
        path="/learn/change-password"
      />

      <section className="min-h-[calc(100vh-5rem)] bg-slate-50 py-12 md:py-20">
        <div className="container-rail">
          <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy text-white">
                <KeyRound className="h-6 w-6" />
              </div>

              <div>
                <p className="label-eyebrow text-safety">
                  Account security
                </p>

                <h1 className="mt-1 font-display text-3xl font-bold text-navy">
                  Create a new password
                </h1>

                <p className="mt-3 text-slate-600">
                  Your temporary password must be replaced before you can
                  access learning.
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-safety" />

              <p>
                Use at least 12 characters including uppercase, lowercase,
                a number and a symbol.
              </p>
            </div>

            <form
              className="mt-7 space-y-5"
              onSubmit={handleSubmit}
            >
              <div>
                <label
                  htmlFor="current-password"
                  className="text-sm font-semibold text-navy"
                >
                  Current password
                </label>

                <input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) =>
                    setCurrentPassword(event.target.value)
                  }
                  required
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none transition focus:border-safety focus:ring-2 focus:ring-safety/20"
                />
              </div>

              <div>
                <label
                  htmlFor="new-password"
                  className="text-sm font-semibold text-navy"
                >
                  New password
                </label>

                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) =>
                    setNewPassword(event.target.value)
                  }
                  required
                  minLength={12}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none transition focus:border-safety focus:ring-2 focus:ring-safety/20"
                />
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="text-sm font-semibold text-navy"
                >
                  Confirm new password
                </label>

                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) =>
                    setConfirmPassword(event.target.value)
                  }
                  required
                  minLength={12}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none transition focus:border-safety focus:ring-2 focus:ring-safety/20"
                />
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-navy px-5 py-3.5 font-bold text-white transition hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting
                  ? "Updating…"
                  : "Update password"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}